/* CasMarkt homepage motion — reveal, count-up, parallax, marquee pause.
   Vanilla, no dependencies, loaded with defer. Safe to run more than once. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: null };
  var desktop = window.matchMedia ? window.matchMedia('(min-width: 990px)') : { matches: true };

  var DONE = 'cmInit';

  // Lets the stylesheet hide .cm-reveal only when this file actually loaded.
  document.documentElement.classList.add('cm-js');

  function each(root, selector, fn) {
    var nodes = root.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i], i);
  }

  function claim(el, key) {
    var flag = DONE + key;
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  }

  /* --- reveal --- */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
          if (entry.target.hasAttribute('data-cm-count')) countUp(entry.target);
        });
      },
      /* v1.7 item 3: -8%, not -10%, and paired with the in-viewport check
         below, so nothing that is already on screen waits to be revealed.
         threshold 0 (P's request), not 0.1: a band taller than the viewport
         can never show 10% of itself at once, so it stayed hidden until the
         visitor scrolled past it — the "half-revealed section" the owner saw. */
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );
    return revealObserver;
  }

  /* Anything the visitor can already see when the page loads must be visible
     immediately — an IntersectionObserver only fires on its first frame, and
     a slow first paint made those sections read as "half-revealed". */
  function inViewportNow(el) {
    if (!el.getBoundingClientRect) return false;
    var box = el.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) return false;
    var height = window.innerHeight || document.documentElement.clientHeight || 0;
    return box.top < height && box.bottom > 0;
  }

  function initReveal(root) {
    var observer = getRevealObserver();

    each(root, '.cm-reveal', function (el) {
      if (!claim(el, 'Reveal')) return;

      // Stagger: keep an author-set value, else use sibling order.
      if (!el.style.getPropertyValue('--cm-stagger')) {
        var parent = el.parentElement;
        var index = 0;
        if (parent) {
          var siblings = parent.querySelectorAll(':scope > .cm-reveal');
          for (var i = 0; i < siblings.length; i++) {
            if (siblings[i] === el) {
              index = i;
              break;
            }
          }
        }
        el.style.setProperty('--cm-stagger', String(index));
      }

      if (reduceMotion.matches || !observer || inViewportNow(el)) {
        el.classList.add('is-in');
        if (el.hasAttribute('data-cm-count')) countUp(el);
        return;
      }
      observer.observe(el);
    });

    // Count-up targets that are not reveal elements themselves.
    each(root, '[data-cm-count]', function (el) {
      if (el.classList.contains('cm-reveal')) return;
      if (!claim(el, 'Count')) return;
      if (reduceMotion.matches || !observer) {
        settleCount(el);
        return;
      }
      var solo = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            solo.unobserve(entry.target);
            countUp(entry.target);
          });
        },
        { threshold: 0.4 }
      );
      solo.observe(el);
    });
  }

  /* --- count-up --- */
  function parseCount(el) {
    var raw = el.getAttribute('data-cm-count') || el.textContent || '';
    var match = raw.match(/^([^0-9]*)([0-9][0-9.,]*)(.*)$/);
    if (!match) return null;
    var digits = match[2];
    var grouped = digits.indexOf(',') !== -1;
    var plain = digits.replace(/,/g, '');
    var value = parseFloat(plain);
    if (isNaN(value)) return null;
    var dot = plain.indexOf('.');
    return {
      prefix: match[1],
      suffix: match[3],
      value: value,
      decimals: dot === -1 ? 0 : plain.length - dot - 1,
      grouped: grouped
    };
  }

  function format(parts, value) {
    var text = value.toFixed(parts.decimals);
    if (parts.grouped) {
      var halves = text.split('.');
      halves[0] = halves[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      text = halves.join('.');
    }
    return parts.prefix + text + parts.suffix;
  }

  function settleCount(el) {
    var parts = parseCount(el);
    if (parts) el.textContent = format(parts, parts.value);
  }

  function countUp(el) {
    if (!claim(el, 'Counted')) return;
    var parts = parseCount(el);
    if (!parts) return;
    if (reduceMotion.matches || !window.requestAnimationFrame) {
      el.textContent = format(parts, parts.value);
      return;
    }

    var duration = 1200;
    var start = 0;

    function step(now) {
      if (!start) start = now;
      var progress = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      el.textContent = format(parts, parts.value * eased);
      if (progress < 1) window.requestAnimationFrame(step);
      else el.textContent = format(parts, parts.value);
    }

    el.textContent = format(parts, 0);
    window.requestAnimationFrame(step);
  }

  /* --- parallax --- */
  var parallaxNodes = [];
  var parallaxTicking = false;

  function updateParallax() {
    parallaxTicking = false;
    var viewport = window.innerHeight || 1;
    for (var i = 0; i < parallaxNodes.length; i++) {
      var el = parallaxNodes[i];
      if (!el.isConnected) continue;
      var box = el.getBoundingClientRect();
      if (box.bottom < 0 || box.top > viewport) continue;
      var max = parseFloat(el.getAttribute('data-cm-parallax')) || 24;
      var centre = (box.top + box.height / 2) / viewport;
      var offset = (0.5 - centre) * 2 * max;
      if (offset > max) offset = max;
      if (offset < -max) offset = -max;
      el.style.transform = 'translate3d(0,' + offset.toFixed(2) + 'px,0)';
    }
  }

  function requestParallax() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    window.requestAnimationFrame(updateParallax);
  }

  function clearParallax() {
    for (var i = 0; i < parallaxNodes.length; i++) parallaxNodes[i].style.transform = '';
  }

  function initParallax(root) {
    each(root, '[data-cm-parallax]', function (el) {
      if (!claim(el, 'Parallax')) return;
      parallaxNodes.push(el);
    });

    if (!parallaxNodes.length) return;

    if (reduceMotion.matches || !desktop.matches || !window.requestAnimationFrame) {
      clearParallax();
      return;
    }
    requestParallax();
  }

  /* --- marquee --- */
  function initMarquee(root) {
    each(root, '[data-cm-marquee]', function (el) {
      if (!claim(el, 'Marquee')) return;
      var track = el.querySelector('.cm-marquee__track, .cm-quotes__track');
      if (!track) return;

      var pause = function () {
        track.classList.add('is-paused');
      };
      var play = function () {
        track.classList.remove('is-paused');
      };

      el.addEventListener('mouseenter', pause);
      el.addEventListener('mouseleave', play);
      el.addEventListener('focusin', pause);
      el.addEventListener('focusout', play);
      el.addEventListener('touchstart', pause, { passive: true });
      el.addEventListener('touchend', play, { passive: true });
    });
  }

  /* --- sticky header: floating pill bar past 40px (DESIGN.md v1.3 item 3) ---
     v1.6 item 2: the compact pill is narrower than the full-bleed bar, so
     anything that measures the header row has to be told when the state
     flips. Listeners register through onHeaderState. */
  var headerEl = null;
  var headerTicking = false;
  var headerScrolled = null;
  var headerStateHooks = [];

  function onHeaderState(fn) {
    headerStateHooks.push(fn);
  }

  function updateHeader() {
    headerTicking = false;
    if (!headerEl) return;
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var next = y > 40;
    if (next === headerScrolled) return;
    headerScrolled = next;
    headerEl.classList.toggle('cm-header--scrolled', next);
    for (var i = 0; i < headerStateHooks.length; i++) {
      try {
        headerStateHooks[i]();
      } catch (error) {
        /* a broken listener must never stop the scroll handler */
      }
    }
  }

  function requestHeader() {
    if (headerTicking) return;
    headerTicking = true;
    window.requestAnimationFrame(updateHeader);
  }

  function initHeader() {
    var el = document.querySelector('.section-header');
    if (!el || !claim(el, 'Header')) return;
    headerEl = el;
    updateHeader();
  }

  /* --- hero headline entrance, word by word. award: choreographed-reveals ---
     The h1 keeps one aria-label so assistive tech reads a sentence, not a
     list of words. Only the h1 is ever split. */
  function splitWords(el) {
    var index = 0;

    function walk(node) {
      var kids = Array.prototype.slice.call(node.childNodes);
      for (var i = 0; i < kids.length; i++) {
        var child = kids[i];
        if (child.nodeType === 3) {
          var parts = String(child.nodeValue || '').split(/(\s+)/);
          var frag = document.createDocumentFragment();
          for (var p = 0; p < parts.length; p++) {
            if (!parts[p]) continue;
            if (/^\s+$/.test(parts[p])) {
              frag.appendChild(document.createTextNode(parts[p]));
              continue;
            }
            var span = document.createElement('span');
            span.className = 'cm-word';
            span.style.setProperty('--cm-word', String(index++));
            span.textContent = parts[p];
            frag.appendChild(span);
          }
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1) {
          walk(child);
        }
      }
    }

    walk(el);
  }

  function initWords(root) {
    each(root, '[data-cm-words]', function (el) {
      if (!claim(el, 'Words')) return;

      var label = String(el.textContent || '').replace(/\s+/g, ' ').replace(/^ | $/g, '');
      if (label && !el.getAttribute('aria-label')) el.setAttribute('aria-label', label);
      if (reduceMotion.matches) return;

      el.classList.add('cm-words');
      splitWords(el);

      var show = function () {
        el.classList.add('is-in');
      };

      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(show);
        window.setTimeout(show, 1200); // never wait on a font that never loads
      } else {
        show();
      }
    });
  }

  /* --- living Texas dispatch line. award: living-dispatch ---
     Liquid ships a static fallback; this only upgrades it. Never announced. */
  function initDispatch(root) {
    each(root, '[data-cm-dispatch]', function (el) {
      if (!claim(el, 'Dispatch')) return;

      var timeEl = el.querySelector('[data-cm-dispatch-time]');
      var clockEl = el.querySelector('[data-cm-dispatch-clock]');
      var msgEl = el.querySelector('[data-cm-dispatch-msg]');
      if (!timeEl || !msgEl || !window.Intl || !window.Intl.DateTimeFormat) return;

      var before = el.getAttribute('data-cm-before') || '';
      var after = el.getAttribute('data-cm-after') || '';
      var weekend = el.getAttribute('data-cm-weekend') || '';
      if (!before && !after && !weekend) return;

      var zone = el.getAttribute('data-cm-tz') || 'America/Chicago';
      var cutoff = String(el.getAttribute('data-cm-cutoff') || '14:00').split(':');
      var cutoffAt = (parseInt(cutoff[0], 10) || 0) * 60 + (parseInt(cutoff[1], 10) || 0);

      var clock;
      var fields;
      try {
        clock = new Intl.DateTimeFormat('en-US', {
          timeZone: zone,
          hour: 'numeric',
          minute: '2-digit'
        });
        fields = new Intl.DateTimeFormat('en-US', {
          timeZone: zone,
          weekday: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hour12: false
        });
        if (!fields.formatToParts) return;
      } catch (error) {
        return;
      }

      var shown = '';

      function tick() {
        var now = new Date();
        var stamp = clock.format(now);
        var parts = fields.formatToParts(now);
        var day = '';
        var hour = 0;
        var minute = 0;

        for (var i = 0; i < parts.length; i++) {
          if (parts[i].type === 'weekday') day = parts[i].value;
          else if (parts[i].type === 'hour') hour = (parseInt(parts[i].value, 10) || 0) % 24;
          else if (parts[i].type === 'minute') minute = parseInt(parts[i].value, 10) || 0;
        }

        var message;
        if (day === 'Sat' || day === 'Sun') message = weekend;
        else message = hour * 60 + minute < cutoffAt ? before : after;
        if (!message) message = weekend || after || before;

        if (stamp !== shown) {
          shown = stamp;
          timeEl.textContent = stamp;
          if (clockEl) clockEl.hidden = false;
          if (!reduceMotion.matches) {
            timeEl.classList.remove('is-ticking');
            void timeEl.offsetWidth;
            timeEl.classList.add('is-ticking');
          }
        }

        var line = '· ' + message;
        if (msgEl.textContent !== line) msgEl.textContent = line;
      }

      tick();
      window.setInterval(tick, 30000);
    });
  }

  /* --- header search pill (v1.6 items 2 and 8) ---
     A visible control between the menu and the icons. It does NOT reimplement
     search: it forwards its click to Spotlight's own <summary>, so the same
     details-modal, predictive search and focus trap run as before. Injected
     rather than templated so sections/header.liquid stays close to stock;
     without JS the stock magnifier is still there.

     v1.6 item 8 removed the "/" chip and the global keydown shortcut - the
     owner wants to click it. v1.6 item 2 moved it out of .header__icons and
     into the header row itself, where the desktop flex layout can shrink it
     before the menu is ever able to collide with the icons. */
  var SEARCH_WORDS = ['knee highs\u2026', 'bamboo\u2026', 'value packs\u2026', 'finds\u2026'];

  function initSearchPill() {
    var header = document.querySelector('.section-header .header');
    if (!header || !claim(header, 'SearchPill')) return;

    var summary =
      header.querySelector('.header__icons .header__search summary') ||
      header.querySelector('.header__search summary');
    var icons = header.querySelector('.header__icons');
    if (!summary || !icons) return;

    var pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'cm-searchpill';
    pill.setAttribute('aria-haspopup', 'dialog');
    pill.setAttribute('aria-label', 'Search');
    pill.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<circle cx="11" cy="11" r="6.4" stroke="currentColor" stroke-width="1.8"/>' +
      '<path d="m16 16 4.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
      '</svg>' +
      '<span class="cm-searchpill__label" aria-hidden="true">Search ' +
      '<span class="cm-searchpill__word"></span></span>' +
      '<span class="cm-searchpill__caret" aria-hidden="true"></span>';

    var wordEl = pill.querySelector('.cm-searchpill__word');
    wordEl.textContent = 'socks, packs, finds\u2026';

    var modal = summary.closest ? summary.closest('details-modal') : null;
    var input = modal ? modal.querySelector('input[type="search"]') : null;

    function openSearch() {
      summary.click();
      if (!input) return;
      window.setTimeout(function () {
        try {
          input.focus();
        } catch (error) {
          /* the modal owns focus management; this is only a nicety */
        }
      }, 60);
    }

    /* stopPropagation matters: Spotlight's details-modal binds a body click
       listener the moment it opens, and this click is still bubbling. Left
       alone it reaches <body>, is judged "outside the modal", and closes the
       panel in the same tick it opened. */
    pill.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      openSearch();
    });

    /* In the row itself, before the icon cluster: the desktop header is a flex
       row (cm-header.css section 3) so the pill is the one elastic child and
       shrinks - then collapses to a circle - long before the menu could reach
       the icons. */
    header.insertBefore(pill, icons);
    header.classList.add('cm-has-searchpill');

    /* --- does the row have room? ---------------------------------------
       Measured rather than hard-coded, because the owner can add a top-level
       menu item at any time (the store already has eight). Re-runs on resize,
       after web fonts land, and whenever the bar flips to its scrolled state.

       v1.7 item 1 asks for generous menu spacing - gap clamp(18px, 1.8vw,
       34px), 10px/14px item padding and at least 28px before the search pill.
       Eight items cannot hold all of that at 1280, so cm-header.css declares
       the roomy values plus three progressively tighter fallbacks and this
       function picks the widest one that actually fits. Tightening the menu
       comes FIRST, before the search is touched: it is what stops the menu
       from ever running into the icons, which is what the owner reported. */
    var TIGHT = ['cm-menu--t1', 'cm-menu--t2', 'cm-menu--t3', 'cm-menu--t4'];
    /* Both figures are measured against .cm-measuring, where the pill has no
       width and no margin, so the gap read back is 2 flex gaps (2 x 16px)
       plus whatever slack is left. A circle therefore needs 32 + the 28px
       v1.7 gap + 40px; the labelled pill needs 32 + 28 + 190. */
    var FIT_ICON = 100;
    var FIT_PILL = 250;
    var menu = header.querySelector('.header__inline-menu');

    function overflowing() {
      return header.scrollWidth - header.clientWidth > 1;
    }

    function fitPill() {
      pill.classList.remove('is-compact');
      header.classList.remove('cm-search-hidden');
      header.classList.remove('cm-measuring');
      for (var t = 0; t < TIGHT.length; t++) header.classList.remove(TIGHT[t]);
      if (window.innerWidth < 990) return;

      /* Step 1: how tight does the MENU have to be to fit at all? Measured
         with the stock magnifier back in the row (.cm-search-hidden), which
         is the narrowest the row can ever be. Reading scrollWidth flushes
         layout, so each pass sees the class added by the previous one. */
      header.classList.add('cm-search-hidden');

      for (var i = 0; i < TIGHT.length && overflowing(); i++) {
        if (i > 0) header.classList.remove(TIGHT[i - 1]);
        header.classList.add(TIGHT[i]);
      }

      var stillOver = overflowing();
      header.classList.remove('cm-search-hidden');
      if (stillOver) {
        /* Eight items at the tightest step still overflow: give the row back
           to the menu and let Spotlight's own magnifier be the search. */
        header.classList.add('cm-search-hidden');
        return;
      }

      /* Step 2: at that spacing, is there room for the pill itself? */
      header.classList.add('cm-measuring');
      var menuRight = menu ? menu.getBoundingClientRect().right : 0;
      var free = icons.getBoundingClientRect().left - menuRight;
      header.classList.remove('cm-measuring');

      if (free < FIT_ICON) {
        header.classList.add('cm-search-hidden');
        return;
      }
      if (free < FIT_PILL) pill.classList.add('is-compact');
    }

    fitPill();
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(fitPill).catch(function () {});
    }
    /* Bricolage can land after fonts.ready resolves in some engines; one late
       pass costs nothing and stops a first paint from sticking. */
    window.setTimeout(fitPill, 900);

    var fitTimer = 0;
    function scheduleFit() {
      window.clearTimeout(fitTimer);
      fitTimer = window.setTimeout(fitPill, 120);
    }

    window.addEventListener('resize', scheduleFit, { passive: true });
    onHeaderState(scheduleFit);

    /* --- typewriter placeholder (v1.6 item 8) ---------------------------
       Decorative only: the label is aria-hidden and the button keeps a fixed
       accessible name. Static text under reduced motion. */
    if (reduceMotion.matches) return;

    var wordIndex = 0;
    var chars = 0;
    var deleting = false;
    var typeTimer = 0;

    function later(ms) {
      window.clearTimeout(typeTimer);
      typeTimer = window.setTimeout(type, ms);
    }

    function type() {
      if (document.hidden) {
        later(600);
        return;
      }
      var word = SEARCH_WORDS[wordIndex];
      if (deleting) {
        chars -= 1;
        wordEl.textContent = word.slice(0, Math.max(chars, 0));
        if (chars <= 0) {
          deleting = false;
          wordIndex = (wordIndex + 1) % SEARCH_WORDS.length;
          later(280);
          return;
        }
        later(30);
        return;
      }
      chars += 1;
      wordEl.textContent = word.slice(0, chars);
      if (chars >= word.length) {
        deleting = true;
        later(1800);
        return;
      }
      later(55);
    }

    wordEl.textContent = '';
    later(900);
  }

  /* --- mega menu (v1.6 item 6) ---
     A parent with children is a real <a> now, so clicking "Women's Socks"
     lands on the collection and the panel is opened by intent instead: hover
     after 80ms, close after 120ms so crossing between two parents never
     flickers, Down arrow opens and moves into the panel, Escape closes.
     The ARIA that details/summary used to provide for free is written on the
     trigger and kept in sync here. */
  function initMegaMenu() {
    var nav = document.querySelector('.header__inline-menu');
    if (!nav || !claim(nav, 'MegaMenu')) return;

    var items = nav.querySelectorAll('[data-cm-mega]');
    if (!items.length) return;

    var wide = window.matchMedia ? window.matchMedia('(min-width: 990px)') : { matches: true };
    var hoverable = window.matchMedia
      ? window.matchMedia('(hover: hover) and (pointer: fine)')
      : { matches: true };

    var openTimer = 0;
    var closeTimer = 0;
    /* Escape closes the panel and hands focus back to the parent link, but
       focus() fires focusin synchronously and the li's focusin handler below
       reopens whatever contains the focus — so Escape used to close and
       reopen in the same tick and looked like it did nothing. The flag is
       raised only around that one focus() call. */
    var suppressFocusOpen = false;

    function setOpen(li, open) {
      if (!li) return;
      li.classList.toggle('is-open', open);
      var trigger = li.querySelector('[data-cm-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function closeAll(except) {
      for (var i = 0; i < items.length; i++) {
        if (items[i] !== except) setOpen(items[i], false);
      }
    }

    function openLater(li, delay) {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      openTimer = window.setTimeout(function () {
        closeAll(li);
        setOpen(li, true);
      }, delay);
    }

    function closeLater(li, delay) {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        if (li.contains(document.activeElement)) return;
        setOpen(li, false);
      }, delay);
    }

    for (var i = 0; i < items.length; i++) {
      (function (li) {
        var trigger = li.querySelector('[data-cm-mega-trigger]');
        var panel = li.querySelector('[data-cm-mega-panel]');

        li.addEventListener('mouseenter', function () {
          if (!wide.matches || !hoverable.matches) return;
          openLater(li, 80);
        });

        li.addEventListener('mouseleave', function () {
          if (!wide.matches || !hoverable.matches) return;
          closeLater(li, 120);
        });

        li.addEventListener('focusin', function () {
          if (!wide.matches || suppressFocusOpen) return;
          window.clearTimeout(closeTimer);
          closeAll(li);
          setOpen(li, true);
        });

        if (trigger) {
          trigger.addEventListener('keydown', function (event) {
            if (event.key === 'ArrowDown' || event.key === 'Down') {
              event.preventDefault();
              closeAll(li);
              setOpen(li, true);
              var first = panel ? panel.querySelector('a[href]') : null;
              if (first) first.focus();
            } else if (event.key === 'Escape' || event.key === 'Esc') {
              setOpen(li, false);
            }
          });
        }

        if (panel) {
          panel.addEventListener('keydown', function (event) {
            if (event.key !== 'Escape' && event.key !== 'Esc') return;
            setOpen(li, false);
            if (!trigger) return;
            suppressFocusOpen = true;
            trigger.focus();
            suppressFocusOpen = false;
          });
        }
      })(items[i]);
    }

    /* Focus or a click landing outside the nav closes everything, so a panel
       can never be left hanging over the page. */
    document.addEventListener('focusin', function (event) {
      if (nav.contains(event.target)) return;
      closeAll(null);
    });

    document.addEventListener('click', function (event) {
      if (nav.contains(event.target)) return;
      closeAll(null);
    });

    onHeaderState(function () {
      closeAll(null);
    });
  }

  /* --- page transitions: the 2px pink line (v1.6 item 6) ---
     The cross-document view transition itself is pure CSS (@view-transition
     in cm-theme.css, the 12px content rise in cm-header.css). This only adds
     the progress line, which starts on a same-origin link click and completes
     as the old page goes away. */
  function initNavProgress() {
    if (reduceMotion.matches || !document.body) return;
    if (!claim(document.body, 'NavProgress')) return;

    var bar = document.createElement('div');
    bar.className = 'cm-navprogress';
    document.body.appendChild(bar);

    var running = false;
    var resetTimer = 0;

    function start() {
      if (running) return;
      running = true;
      window.clearTimeout(resetTimer);
      bar.classList.remove('is-done');
      void bar.offsetWidth;
      bar.classList.add('is-active');
    }

    function finish() {
      if (!running) return;
      running = false;
      bar.classList.remove('is-active');
      bar.classList.add('is-done');
      resetTimer = window.setTimeout(function () {
        bar.classList.remove('is-done');
      }, 600);
    }

    document.addEventListener(
      'click',
      function (event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        var target = event.target;
        var link = target && target.closest ? target.closest('a[href]') : null;
        if (!link || link.hasAttribute('download')) return;
        if (link.target && link.target !== '_self') return;
        var href = link.getAttribute('href') || '';
        if (!href || href.charAt(0) === '#') return;
        if (/^(mailto:|tel:|javascript:|sms:)/i.test(href)) return;
        if (link.origin !== window.location.origin) return;
        if (link.pathname === window.location.pathname && link.search === window.location.search) {
          return;
        }
        start();
      },
      true
    );

    window.addEventListener('pagehide', finish);
    window.addEventListener('pageshow', finish);
    window.addEventListener('popstate', finish);
  }

  /* --- rails: drag to scroll, prev/next, edge state --- */
  function initRails(root) {
    each(root, '[data-cm-rail]', function (rail) {
      if (!claim(rail, 'Rail')) return;
      var track = rail.querySelector('.cm-rail__track');
      if (!track) return;

      var band = rail.closest ? rail.closest('.cm-band') || rail.parentNode : rail.parentNode;
      var prev = band.querySelector('[data-cm-rail-prev]');
      var next = band.querySelector('[data-cm-rail-next]');

      function step() {
        var card = track.firstElementChild;
        var gap = parseFloat(window.getComputedStyle(track).columnGap) || 16;
        return card ? card.offsetWidth + gap : Math.round(track.clientWidth * 0.8);
      }

      function sync() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }

      function go(direction) {
        var amount = direction * step();
        if (track.scrollBy) {
          track.scrollBy({ left: amount, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        } else {
          track.scrollLeft += amount;
        }
      }

      if (prev) {
        prev.addEventListener('click', function () {
          go(-1);
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          go(1);
        });
      }
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();

      // Drag with a mouse or pen; touch keeps the browser's own momentum.
      var dragging = false;
      var startX = 0;
      var startLeft = 0;
      var moved = false;

      track.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        rail.classList.add('is-dragging');
      });

      /* v2.2: an 8px dead zone — a normal click always wobbles a pixel or
         two, and the old 3px guard swallowed real clicks on rail cards. The
         rail only starts scrolling once the pointer has clearly dragged. */
      track.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - startX;
        if (!moved && Math.abs(dx) <= 8) return;
        moved = true;
        track.scrollLeft = startLeft - dx;
      });

      function endDrag() {
        if (!dragging) return;
        dragging = false;
        rail.classList.remove('is-dragging');
        /* Let the click that follows this pointerup see `moved`, then clear it
           so the next plain click is never blocked by a stale drag. */
        window.setTimeout(function () {
          moved = false;
        }, 0);
      }

      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (name) {
        track.addEventListener(name, endDrag);
      });

      // A drag that ends on a card must not follow the link.
      track.addEventListener(
        'click',
        function (event) {
          if (!moved) return;
          moved = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    });
  }

  /* --- video rail: play the card in view, muted; tap for sound --- */
  function initVideoRails(root) {
    each(root, '[data-cm-video-rail]', function (rail) {
      if (!claim(rail, 'VideoRail')) return;

      var videos = [];
      each(rail, '.cm-vcard video', function (video) {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        video.removeAttribute('controls');
        videos.push(video);
      });
      if (!videos.length) return;

      function card(video) {
        return video.closest ? video.closest('.cm-vcard') : null;
      }

      function play(video) {
        var started = video.play();
        if (started && started.catch) started.catch(function () {});
        var host = card(video);
        if (host) host.classList.add('is-playing');
      }

      function setSound(video, on) {
        video.muted = !on;
        var host = card(video);
        var button = host && host.querySelector('[data-cm-sound]');
        if (!button) return;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        var label = button.querySelector('[data-cm-sound-label]');
        if (label) label.textContent = on ? 'Turn sound off' : 'Turn sound on';
      }

      each(rail, '[data-cm-sound]', function (button) {
        button.addEventListener('click', function () {
          var host = button.closest('.cm-vcard');
          var video = host && host.querySelector('video');
          if (!video) return;
          var wantSound = video.muted;
          for (var i = 0; i < videos.length; i++) {
            if (videos[i] !== video) setSound(videos[i], false);
          }
          setSound(video, wantSound);
          if (wantSound) play(video);
        });
      });

      // Reduced motion: nothing starts on its own, so hand over native controls.
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        for (var i = 0; i < videos.length; i++) videos[i].controls = true;
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              play(video);
            } else {
              video.pause();
              if (!video.muted) setSound(video, false);
            }
          });
        },
        { threshold: [0, 0.6, 0.9] }
      );

      for (var v = 0; v < videos.length; v++) observer.observe(videos[v]);
    });
  }

  /* --- boot --- */
  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    initWords(scope);
    initReveal(scope);
    initParallax(scope);
    initMarquee(scope);
    initHeader();
    initSearchPill();
    initMegaMenu();
    initNavProgress();
    initRails(scope);
    initVideoRails(scope);
    initDispatch(scope);
  }

  function onReduceMotionChange() {
    if (!reduceMotion.matches) return;
    clearParallax();
    each(document, '.cm-reveal', function (el) {
      el.classList.add('is-in');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });
  window.addEventListener('scroll', requestHeader, { passive: true });

  if (reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', onReduceMotionChange);
  } else if (reduceMotion.addListener) {
    reduceMotion.addListener(onReduceMotionChange);
  }

  // Theme editor swaps sections in without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
    requestParallax();
  });

  document.addEventListener('shopify:section:unload', function () {
    parallaxNodes = parallaxNodes.filter(function (el) {
      return el.isConnected;
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target;
    if (el && el.classList && el.classList.contains('cm-reveal')) el.classList.add('is-in');
  });
})();
